import type { HelpdeskProvider, IHelpdeskConfig, IHelpdeskLimits } from "@kuika/kuika-cl-model";
import { useEffect, useState } from "react";
import { HelpdeskService } from "../../../services/helpdesk-service";

/** The same limits CoreApi reports; used until (or if) the config call answers. */
export const DEFAULT_HELPDESK_LIMITS: IHelpdeskLimits = {
  subjectMaxLength: 300,
  descriptionMaxLength: 20000,
  appMailMaxLength: 320,
  appPasswordMaxLength: 200,
  maxFileBytes: 50 * 1024 * 1024,
  maxFilesPerTicket: 100,
  maxTotalBytesPerTicket: 300 * 1024 * 1024
};

/** The ITSM's ticket-context limits (contract §3.1): names up to 300, ids up to 100 characters. */
export const HELPDESK_CONTEXT_NAME_MAX_LENGTH: number = 300;
export const HELPDESK_CONTEXT_ID_MAX_LENGTH: number = 100;

const ZENDESK_CONFIG: IHelpdeskConfig = { provider: "Zendesk", limits: DEFAULT_HELPDESK_LIMITS };

let pending: Promise<IHelpdeskConfig> | null = null;
let loaded: IHelpdeskConfig | null = null;

/**
 * Which helpdesk CoreApi routes to (`HELPDESK_PROVIDER`), fetched once per page load. A failed call
 * falls back to Zendesk — today's behaviour — and is not cached, so the next caller retries.
 */
export function loadHelpdeskConfig(): Promise<IHelpdeskConfig> {
  if (!pending) {
    pending = HelpdeskService.getConfig()
      .then((config: IHelpdeskConfig): IHelpdeskConfig => {
        loaded = {
          provider: config?.provider === "Itsm" ? "Itsm" : "Zendesk",
          limits: { ...DEFAULT_HELPDESK_LIMITS, ...(config?.limits ?? {}) }
        };
        return loaded;
      })
      .catch((): IHelpdeskConfig => {
        pending = null;
        return ZENDESK_CONFIG;
      });
  }
  return pending;
}

/**
 * The provider, once the config has answered; null before that or after a failed call. For
 * synchronous layout decisions; anything that can wait should use `loadHelpdeskConfig`.
 */
export function loadedHelpdeskProvider(): HelpdeskProvider | null {
  return loaded?.provider ?? null;
}

/** Test seam: forget the cached config. */
export function resetHelpdeskConfigCache(): void {
  pending = null;
  loaded = null;
}

/** The helpdesk config, or null while it loads. */
export function useHelpdeskConfig(): IHelpdeskConfig | null {
  const [config, setConfig] = useState<IHelpdeskConfig | null>(null);

  useEffect((): (() => void) => {
    let active: boolean = true;
    loadHelpdeskConfig().then((loaded: IHelpdeskConfig): void => {
      if (active) setConfig(loaded);
    });
    return (): void => {
      active = false;
    };
  }, []);

  return config;
}
